/**
 * useLegalPage — shared data-fetching composable for static/legal pages.
 *
 * Replaces the duplicated pattern that previously existed in every standalone
 * legal (and homework) page:
 *   - live GraphQL `pageBy(uri: ...)` call in useAsyncData / onMounted
 *   - replaceLinks / cleanJsonLd helpers
 *   - useHead with canonical + JSON-LD
 *   - TOC toggle logic in onMounted
 *
 * Now reads from the S3-backed `/api/dictionary/pages` endpoint (same source
 * used by the catch-all route).  A Songbird self-heal is already wired into
 * that endpoint — if a slug isn't in S3 yet the first request will populate it
 * automatically.  The build-time script `utils/backfill-legal-pages.js`
 * pre-warms S3 on every deploy so crawlers see fast SSR responses immediately.
 *
 * Usage (inside <script setup>):
 *   const { pageTitle, pageContent } = await useLegalPage('terms-of-service');
 *
 * The `wpSlug` parameter must match the WordPress REST API slug
 * (i.e. the path component used in the original `pageBy(uri: "/<wpSlug>/")`)
 * which is usually identical to the Nuxt route name, except for
 * editorial-guidelines → 'editorialguidelines'.
 */

import { computed, onMounted, nextTick } from "vue";

// ---------------------------------------------------------------------------
// Private helpers (not exported — callers only need the composable)
// ---------------------------------------------------------------------------

function replaceLinks(content: string | undefined): string {
	if (!content) return "";
	return content.replace(/https:\/\/content\.timothysykes\.com/g, "https://www.timothysykes.com");
}

/**
 * The S3 / WordPress REST payload stores JSON-LD under
 * `yoast_head_json.schema_json` (a raw JSON string), mirroring the shape
 * already consumed by `[...widget-page].vue`.
 * The old GraphQL path exposed `saswpSchema.json_ld` (an object).
 * We normalise both and return a cleaned, serialised string or null.
 */
function extractJsonLd(data: any): string | null {
	if (!data) return null;

	// S3 / REST path: yoast_head_json.schema_json is a raw JSON string
	const raw: any = data?.yoast_head_json?.schema_json ?? null;
	if (!raw) return null;

	try {
		const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
		const jsonString = JSON.stringify(parsed);
		return jsonString
			.replace(/\\\"/g, '"')
			.replace(/\\\\/g, "\\")
			.replace(/https:\/\/content\.timothysykes\.com\/?/g, "https://www.timothysykes.com/");
	} catch {
		return null;
	}
}

function setupTocToggle(): void {
	const tocTitles = document.querySelectorAll<HTMLElement>(".toc_title");
	const toc = document.querySelector<HTMLElement>(".toc_list");

	if (tocTitles.length === 0) return;

	const svgExpand = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
		<circle cx="12.5" cy="12.5" r="11.5" fill="#3094FB" stroke="#FCFCFD" stroke-width="2"/>
		<path d="M12.5 7.5V17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
		<path d="M7.5 12.5H17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
	</svg>`;

	const svgCollapse = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
		<circle cx="12.5" cy="12.5" r="11.5" fill="#3094FB" stroke="#FCFCFD" stroke-width="2"/>
		<path d="M7.5 12.5H17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
	</svg>`;

	tocTitles.forEach((tocTitle) => {
		const button = document.createElement("button");
		button.setAttribute("aria-label", "Toggle Table of Contents");
		button.innerHTML = `<span class="expand">${svgExpand}</span><span class="collapse">${svgCollapse}</span>`;

		button.addEventListener("click", function () {
			if (toc?.classList.contains("active")) {
				button.classList.remove("active");
				toc.classList.remove("active");
			} else {
				button.classList.add("active");
				toc?.classList.add("active");
			}
		});
		tocTitle.appendChild(button);
	});
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export async function useLegalPage(wpSlug: string) {
	const $route = useRoute();
	const { current_hostname } = useRuntimeConfig().public;
	// $route.name is the Nuxt route name (derived from the filename, e.g.
	// "terms-of-service") and is what the canonical URL should use — even when
	// `wpSlug` differs (e.g. editorial-guidelines uses 'editorialguidelines').
	const pageSlug = $route.name as string;

	const { data } = await useAsyncData(`legal-page-${wpSlug}`, () =>
		$fetch<any>(`/api/dictionary/pages?slug=${wpSlug}`)
	);

	// S3 payload has flat title/content strings after Songbird normalisation
	// (toFlatRendered in normalizeS3PostToWpShape), but tolerate the legacy
	// { rendered: "..." } object shape from partially-migrated buckets.
	const pageTitle = computed<string>(() => {
		const t = data.value?.title;
		return (typeof t === "string" ? t : (t as any)?.rendered) || "";
	});

	const pageContent = computed<string>(() => {
		const c = data.value?.content;
		const raw = (typeof c === "string" ? c : (c as any)?.rendered) || "";
		return replaceLinks(raw);
	});

	useHead(() => {
		const headConfig: Record<string, any> = {
			title: `${pageTitle.value || ""} - Timothy Sykes`,
			link: [{ rel: "canonical", href: `${current_hostname}${pageSlug}/` }],
		};
		const jsonLd = extractJsonLd(data.value);
		if (jsonLd) {
			headConfig.script = [{ type: "application/ld+json", innerHTML: jsonLd }];
		}
		return headConfig;
	});

	// Wire the TOC toggle after the content has rendered.  Guards against SSR
	// where `document` is unavailable.
	onMounted(() => {
		if (data.value?.content) {
			nextTick(() => {
				setupTocToggle();
			});
		}
	});

	return { data, pageTitle, pageContent };
}
